import { rem } from 'polished';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Box, Header as HeaderBokeh, Paragraph as ParagraphBokeh } from '@vimeo/bokeh';
import {
  ForbiddenError,
  RateLimitError,
  ResourceNotFoundError,
  UnauthorizedError,
  NotAvailableError } from
'@vimeo/core/libs/errors';
import { translate } from '@vimeo/i18n-runtime';
import { Header, Paragraph } from '@vimeo/iris/typography';
import { type ErrorPageDisplayedProps, useErrorPageTracking, usePicoEffect } from '@vimeo/picox';
import { useAnalyticsEvent } from '@vimeo/use-tracking';

import { SearchField } from './search';

const Container = styled.div`
  margin-top: ${rem(40)};
  text-align: center;
`;

const Title = styled(Header).attrs({ size: '1' })`
  margin-top: 0;
  margin-bottom: ${rem(10)};
`;

const Content = styled.div`
  margin: 0 auto;
  max-width: ${rem(660)};
  padding: 0 ${rem(15)} ${rem(10)};
`;

const Message = styled(Paragraph).attrs({ size: '1' })`
  margin-bottom: ${rem(30)};
  display: block;
`;

const View: React.FC<{title: string;message: string;shouldShowSearch?: boolean;}> = ({
  title,
  message,
  shouldShowSearch = true
}) => {
  // Log the error to the console once
  useEffect(() => {
    // Availability should catch this log worst case scenario
    console.error(`[Vimeo Error Page] ${title}: ${message}`);
  }, [title, message]);

  return (
    // Availability audits rely on this id
    // Do not change unless updating the Availability audits too
    <Container id="vimeo-error-page-view">
      <Title>{title}</Title>
      <Content>
        <Message>{message}</Message>
        {shouldShowSearch && <SearchField id="error-page-search" />}
      </Content>
    </Container>);

};

// Bokeh version of the View component. It was implemented because styled-components based one doesn't look correct in dark mode in bokeh env.
// It looks like we need to keep both versions for a while since the component is used in many apps (outside vimeo-next) which may not be migrated to bokeh yet.
const ViewBokeh: React.FC<{title: string;message: string;shouldShowSearch?: boolean;}> = ({
  title,
  message,
  shouldShowSearch = true
}) => {
  // Log the error to the console once
  useEffect(() => {
    // Availability should catch this log worst case scenario
    console.error(`[Vimeo Error Page] ${title}: ${message}`);
  }, [title, message]);

  return (
    // Availability audits rely on this id
    // Do not change unless updating the Availability audits too
    <Box id="vimeo-error-page-view" textAlign="center" marginTop="500">
      <HeaderBokeh variant="heading-xl" as="h1" marginBottom="100">
        {title}
      </HeaderBokeh>
      <Box margin="0 auto" maxWidth={rem(660)} paddingX="200" paddingBottom="100">
        <ParagraphBokeh marginBottom="400">{message}</ParagraphBokeh>
        {shouldShowSearch && <SearchField id="error-page-search" />}
      </Box>
    </Box>);

};

const getErrorNameFromConstructor = (constructorFunction: Function): string => {
  switch (constructorFunction) {
    case RateLimitError:
      return 'Rate Limit Error';
    case ResourceNotFoundError:
      return 'Resource Not Found Error';
    case ForbiddenError:
      return 'Forbidden Error';
    case UnauthorizedError:
      return 'Unauthorized Error';
    case NotAvailableError:
      return 'Not Available Error';
  }
  return 'Technical Difficulties';
};

enum BPEventNames {
  VISIT_ERROR_PAGE = 'vimeo_com.visit_error_page',
}
interface VisitErrorPageFields {
  error_type: string;
  error_message: string | null;
  url: string;
}

export const visitErrorPageEvent = (fields: VisitErrorPageFields) => ({
  eventName: BPEventNames.VISIT_ERROR_PAGE,
  version: 1,
  fields
});

export interface ErrorPageProps {
  error: Error;
  shouldShowSearch?: boolean;
  useBokeh?: boolean;
}

export function ErrorPage(props: ErrorPageProps) {
  const { error, shouldShowSearch, useBokeh = false } = props;
  const sendAnalyticsEvent = useAnalyticsEvent();
  const { trackErrorPageDisplayed } = useErrorPageTracking();
  // Calculate View component based on prop rather than `useIsBokeh()` hook
  // to avoid having this component updated without code owners knowing about that.
  const ViewComponent = useBokeh ? ViewBokeh : View;

  useEffect(
    () =>
    sendAnalyticsEvent(
      visitErrorPageEvent({
        error_type: getErrorNameFromConstructor(error.constructor),
        error_message: error.message || null,
        url: window.location.href
      })
    ),
    []
  );

  const errorMapping: Pick<ErrorPageDisplayedProps, 'errorType' | 'errorPageErrorCode'> =
  error instanceof RateLimitError ?
  { errorType: 'rate_limit', errorPageErrorCode: 429 } :
  error instanceof ResourceNotFoundError ?
  { errorType: 'resource_not_found', errorPageErrorCode: 404 } :
  error instanceof ForbiddenError ?
  { errorType: 'forbidden', errorPageErrorCode: 403 } :
  error instanceof UnauthorizedError ?
  { errorType: 'unauthorized', errorPageErrorCode: 401 } :
  error instanceof NotAvailableError ?
  { errorType: 'not_available', errorPageErrorCode: 451 } :
  { errorType: 'technical_difficulties', errorPageErrorCode: null };

  usePicoEffect(
    () => {
      trackErrorPageDisplayed({ errorPageType: 'vimeo_next', ...errorMapping });
    },
    [],
    { once: true }
  );
  switch (error.constructor) {
    default:
      if (typeof window !== 'undefined') {
        window.DD_RUM &&
        window.DD_RUM.onReady(() => {
          window.DD_RUM.addError(error, { component: 'TechnicalDifficulties' });
        });
      }

      return (
        <ViewComponent
          title={translate({ singular: 'Technical Difficulties', dictionary: { "es": { singular: "Dificultades t\xE9cnicas" }, "de-DE": { singular: "Technische Schwierigkeiten" }, "fr-FR": { singular: "Difficult\xE9s techniques" }, "ja-JP": { singular: "\u6280\u8853\u7684\u306A\u554F\u984C\u304C\u767A\u751F\u3057\u307E\u3057\u305F" }, "ko-KR": { singular: "\uAE30\uC220\uC801 \uBB38\uC81C" }, "pt-BR": { singular: "Dificuldades T\xE9cnicas" }, "zh-CN": { singular: "\u6280\u672F\u96BE\u9898" } } })}
          message={translate({ singular:
            'Whoops! We are having some technical difficulties, please try again in a minute.', dictionary: { "es": { singular: "\xA1Ups! En estos momentos tenemos algunas dificultades t\xE9cnicas. Por favor, int\xE9ntalo de nuevo dentro de un minuto." }, "de-DE": { singular: "Hoppla! Der Fehlerteufel hat sich eingeschlichen, bitte versuche es in einigen Augenblicken erneut." }, "fr-FR": { singular: "Oups\xA0! Nous rencontrons des difficult\xE9s techniques, veuillez r\xE9essayer dans quelques instants." }, "ja-JP": { singular: "\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F\uFF01\u6280\u8853\u7684\u306A\u554F\u984C\u304C\u767A\u751F\u3057\u3066\u3044\u307E\u3059\u3002\u5C11\u3057\u5F8C\u3067\u518D\u8A66\u884C\u3057\u304F\u3060\u3055\u3044\u3002" }, "ko-KR": { singular: "\uC800\uB7F0! \uAE30\uC220\uC801 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694." }, "pt-BR": { singular: "Opa! Estamos com algumas dificuldades t\xE9cnicas, tente de novo em um minuto." }, "zh-CN": { singular: "\u54CE\u5440\uFF01\u6211\u4EEC\u9047\u5230\u4E86\u4E00\u4E9B\u6280\u672F\u95EE\u9898\uFF0C\u8BF7\u7A0D\u540E\u518D\u8BD5\u3002" } } }
          )}
          shouldShowSearch={shouldShowSearch} />);



    case RateLimitError:
      return (
        <ViewComponent
          title={translate({ singular: 'Whoa there!', dictionary: { "es": { singular: "\xA1Hey!" }, "de-DE": { singular: "Immer langsam!" }, "fr-FR": { singular: "Wow\xA0!" }, "ja-JP": { singular: "\u3061\u3087\u3063\u3068\u5F85\u3063\u3066\uFF01" }, "ko-KR": { singular: "\uD55C \uBC88 \uB354 \uC0DD\uAC01\uD574\uBCF4\uC138\uC694!" }, "pt-BR": { singular: "Calma a\xED!" }, "zh-CN": { singular: "\u54C7\u5594\uFF01" } } })}
          message={translate({ singular:
            'Sorry, you have made too many requests in a short period of time. Please wait a few minutes and try again.', dictionary: { "es": { singular: "Lo sentimos, has realizado demasiadas solicitudes en un corto periodo de tiempo. Espera unos minutos e int\xE9ntalo de nuevo." }, "de-DE": { singular: "Entschuldige, aber du hast innerhalb zu kurzer Zeit zu viele Anfragen gestellt. Bitte warte einige Minuten und versuche es erneut." }, "fr-FR": { singular: "Nous sommes d\xE9sol\xE9s, mais vous avez effectu\xE9 un trop grand nombre de demandes sur une p\xE9riode r\xE9duite. Veuillez patienter quelques minutes, puis r\xE9essayer." }, "ja-JP": { singular: "\u5927\u5909\u7533\u3057\u8A33\u3042\u308A\u307E\u305B\u3093\u3002\u77ED\u6642\u9593\u306B\u5927\u91CF\u306E\u30EA\u30AF\u30A8\u30B9\u30C8\u304C\u9001\u4FE1\u3055\u308C\u307E\u3057\u305F\u3002\u3057\u3070\u3089\u304F\u304A\u5F85\u3061\u3044\u305F\u3060\u3044\u3066\u304B\u3089\u3001\u3082\u3046\u4E00\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002" }, "ko-KR": { singular: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uC9E7\uC740 \uC2DC\uAC04 \uB0B4\uC5D0 \uB108\uBB34 \uB9CE\uC740 \uC694\uCCAD\uC744 \uD588\uC2B5\uB2C8\uB2E4. \uBA87 \uBD84 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694." }, "pt-BR": { singular: "Desculpe, mas voc\xEA fez muitas solicita\xE7\xF5es em um curto per\xEDodo de tempo. Aguarde alguns minutos e tente de novo." }, "zh-CN": { singular: "\u5BF9\u4E0D\u8D77\uFF0C\u60A8\u5728\u77ED\u65F6\u95F4\u5185\u63D0\u51FA\u4E86\u592A\u591A\u8BF7\u6C42\u3002\u8BF7\u8FC7\u51E0\u5206\u949F\u540E\u518D\u91CD\u8BD5\u3002" } } }
          )}
          shouldShowSearch={shouldShowSearch} />);



    case ResourceNotFoundError:
      return (
        <ViewComponent
          title={translate({ singular: 'Sorry, we couldn’t find that page', dictionary: { "es": { singular: "Lo sentimos, no pudimos encontrar esa p\xE1gina" }, "de-DE": { singular: "Leider konnten wir die Seite nicht finden" }, "fr-FR": { singular: "D\xE9sol\xE9, nous n'avons pas pu trouver cette page." }, "ja-JP": { singular: "\u7533\u3057\u8A33\u3042\u308A\u307E\u305B\u3093\u304C\u3001\u30DA\u30FC\u30B8\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3067\u3057\u305F\u3002" }, "ko-KR": { singular: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4, \uD398\uC774\uC9C0\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4" }, "pt-BR": { singular: "Desculpe, n\xE3o conseguimos encontrar essa p\xE1gina" }, "zh-CN": { singular: "\u62B1\u6B49\uFF0C\u6211\u4EEC\u627E\u4E0D\u5230\u8BE5\u9875\u9762" } } })}
          message={translate({ singular: 'Make sure you’ve typed the URL correctly, or try searching Vimeo.', dictionary: { "es": { singular: "Aseg\xFArese de haber escrito bien el URL o intente buscar el video en Vimeo." }, "de-DE": { singular: "Stellen Sie sicher, dass Sie die URL korrekt eingeben oder versuchen Sie Vimeo zu googeln. " }, "fr-FR": { singular: "Assurez-vous d'avoir saisi l'URL correctement, ou essayez une recherche sur Vimeo." }, "ja-JP": { singular: "URL\u304C\u6B63\u3057\u304F\u8A18\u5165\u3055\u308C\u3066\u3044\u308B\u304B\u78BA\u8A8D\u3059\u308B\u304B\u3001Vimeo\u3092\u691C\u7D22\u3057\u3066\u304F\u3060\u3055\u3044\u3002" }, "ko-KR": { singular: "URL\uC744 \uC815\uD655\uD788 \uC785\uB825\uD588\uB294\uC9C0 \uD655\uC778\uD558\uAC70\uB098 Vimeo\uB97C \uAC80\uC0C9\uD574\uBCF4\uC138\uC694." }, "pt-BR": { singular: "Certifique-se de que voc\xEA digitou o URL corretamente ou tente procurar no Vimeo." }, "zh-CN": { singular: "\u8BF7\u786E\u4FDD\u60A8\u8F93\u5165\u7684 URL \u6B63\u786E\u65E0\u8BEF\uFF0C\u6216\u5C1D\u8BD5\u641C\u7D22 Vimeo\u3002" } } })}
          shouldShowSearch={shouldShowSearch} />);



    case ForbiddenError:
      return (
        <ViewComponent
          title={translate({ singular: 'Permission Denied', dictionary: { "es": { singular: "Permiso denegado" }, "de-DE": { singular: "Berechtigung verweigert" }, "fr-FR": { singular: "Autorisation refus\xE9e" }, "ja-JP": { singular: "\u62D2\u5426\u3055\u308C\u307E\u3057\u305F" }, "ko-KR": { singular: "\uC2B9\uC778 \uAC70\uBD80" }, "pt-BR": { singular: "Permiss\xE3o Negada" }, "zh-CN": { singular: "\u6743\u9650\u88AB\u62D2\u7EDD" } } })}
          message={translate({ singular:
            'Sorry but you do not have the proper permission to access this area of Vimeo.', dictionary: { "es": { singular: "No dispones del permiso adecuado para acceder a esta zona de Vimeo." }, "de-DE": { singular: "F\xFCr diesen Bereich bei Vimeo fehlt dir leider die Zugangsberechtigung." }, "fr-FR": { singular: "D\xE9sol\xE9, nous n'avez pas l'autorisation n\xE9cessaire pour acc\xE9der \xE0 cet espace de Vimeo." }, "ja-JP": { singular: "\u5927\u5909\u7533\u3057\u8A33\u3042\u308A\u307E\u305B\u3093\u3002\u3053\u306E Vimeo \u30A8\u30EA\u30A2\u3078\u306E\u30A2\u30AF\u30BB\u30B9\u306F\u8A31\u53EF\u3055\u308C\u3066\u3044\u307E\u305B\u3093\u3002" }, "ko-KR": { singular: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uC774 \uC601\uC5ED\uC744 \uC561\uC138\uC2A4\uD558\uAE30 \uC704\uD574 \uD544\uC694\uD55C \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4." }, "pt-BR": { singular: "Desculpe, mas voc\xEA n\xE3o tem a permiss\xE3o necess\xE1ria para acessar esta \xE1rea do Vimeo." }, "zh-CN": { singular: "\u62B1\u6B49\uFF0C\u60A8\u6CA1\u6709\u8BBF\u95EE Vimeo \u6B64\u533A\u57DF\u7684\u9002\u5F53\u6743\u9650\u3002" } } }
          )}
          shouldShowSearch={shouldShowSearch} />);



    case UnauthorizedError:
      return (
        <ViewComponent
          title={translate({ singular: 'Unauthorized', dictionary: { "es": { singular: "No autorizado" }, "de-DE": { singular: "Nicht genehmigt" }, "fr-FR": { singular: "Non autoris\xE9" }, "ja-JP": { singular: "\u8A31\u53EF\u3055\u308C\u3066\u3044\u307E\u305B\u3093\u3002" }, "ko-KR": { singular: "\uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4" }, "pt-BR": { singular: "N\xE3o autorizado" }, "zh-CN": { singular: "\u672A\u7ECF\u6388\u6743" } } })}
          message={translate({ singular: 'You are unauthorized for this action.', dictionary: { "es": { singular: "No est\xE1s autorizado para llevar a cabo esta acci\xF3n." }, "de-DE": { singular: "Zu dieser Aktion bist du nicht berechtigt." }, "fr-FR": { singular: "Vous n'avez pas l\u2019autorisation requise pour effectuer cette action." }, "ja-JP": { singular: "\u3053\u306E\u30A2\u30AF\u30B7\u30E7\u30F3\u306F\u8A31\u53EF\u3055\u308C\u307E\u305B\u3093" }, "ko-KR": { singular: "\uC774 \uC791\uC5C5\uC5D0 \uB300\uD55C \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4." }, "pt-BR": { singular: "Voc\xEA n\xE3o tem autoriza\xE7\xE3o para realizar esta a\xE7\xE3o." }, "zh-CN": { singular: "\u60A8\u65E0\u6743\u8FDB\u884C\u6B64\u64CD\u4F5C\u3002" } } })}
          shouldShowSearch={shouldShowSearch} />);


    case NotAvailableError:
      return (
        <ViewComponent
          title={translate({ singular: 'This page isn’t available in your region', dictionary: { "es": { singular: "Esta p\xE1gina no est\xE1 disponible en su regi\xF3n." }, "de-DE": { singular: "Diese Seite ist in Ihrer Region nicht verf\xFCgbar" }, "fr-FR": { singular: "Cette page n'est pas disponible dans votre r\xE9gion." }, "ja-JP": { singular: "\u3053\u306E\u30DA\u30FC\u30B8\u306F\u304A\u4F4F\u307E\u3044\u306E\u5730\u57DF\u3067\u306F\u3054\u5229\u7528\u3044\u305F\u3060\u3051\u307E\u305B\u3093" }, "ko-KR": { singular: "\uC774 \uD398\uC774\uC9C0\uB294 \uADC0\uD558\uC758 \uC9C0\uC5ED\uC5D0\uC11C\uB294 \uC774\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }, "pt-BR": { singular: "Esta p\xE1gina n\xE3o est\xE1 dispon\xEDvel na sua regi\xE3o" }, "zh-CN": { singular: "\u60A8\u6240\u5728\u5730\u533A\u65E0\u6CD5\u67E5\u770B\u6B64\u9875\u9762" } } })}
          message=""
          shouldShowSearch={shouldShowSearch} />);


  }
}